import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth, getAuthHeaders } from './AuthContext';
import { useWorkspace, LS_FILM_KEY } from './WorkspaceContext';
import { API_BASE } from '../config/api';

/* ────────────────────────────────────────────────────────────────────────────
 * Persisted film selection helpers (Phase 1).
 *
 * Storage key is shared with WorkspaceContext so cross-context clears
 * (e.g. workspace switch) stay consistent. localStorage access is
 * try/catched because it can throw in private-mode / sandboxed iframes.
 * ──────────────────────────────────────────────────────────────────────── */
function readPersistedFilmId() {
  try {
    return localStorage.getItem(LS_FILM_KEY) || null;
  } catch {
    return null;
  }
}

function writePersistedFilmId(id) {
  try {
    if (id) localStorage.setItem(LS_FILM_KEY, id);
    else localStorage.removeItem(LS_FILM_KEY);
  } catch {
    /* ignore storage errors */
  }
}

const FilmContext = createContext();

export const FilmProvider = ({ children }) => {
  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();
  const [films, setFilms] = useState([]);
  const [currentFilm, setCurrentFilm] = useState(null);
  // Hydrate currentFilmId from localStorage BEFORE first render to keep
  // useScopedNav stable across reloads. Validation (404 / workspace
  // mismatch) happens asynchronously in an effect further down.
  const [currentFilmId, setCurrentFilmIdState] = useState(() => readPersistedFilmId());
  const [loadingFilms, setLoadingFilms] = useState(false);
  const [loadingCurrentFilm, setLoadingCurrentFilm] = useState(false);
  const [error, setError] = useState(null);
  const [filmsLoaded, setFilmsLoaded] = useState(false);

  // Cache: filmId -> filmData
  const [filmCache, setFilmCache] = useState({});
  const filmsRef = React.useRef(films);
  const filmCacheRef = useRef(filmCache);
  const currentFilmRef = useRef(currentFilm);
  const filmsLoadedRef = useRef(false);
  // [E] Shares the in-flight films-fetch promise so concurrent callers
  // (e.g. FilmSwitcher mounting alongside another consumer) don't each fire
  // their own per-workspace fan-out.
  const inFlightFilmsRef = useRef(null);
  const userId = user?.id;

  useEffect(() => {
    filmsRef.current = films;
  }, [films]);

  useEffect(() => {
    filmCacheRef.current = filmCache;
  }, [filmCache]);

  useEffect(() => {
    currentFilmRef.current = currentFilm;
  }, [currentFilm]);

  // Fetch list of films
  const fetchFilms = useCallback(async (force = false) => {
    if (!userId) {
      setFilms([]);
      setFilmsLoaded(false);
      filmsLoadedRef.current = false;
      return [];
    }

    // Prevent redundant fetch if already loaded and not forced
    if (!force && filmsLoadedRef.current) {
      return filmsRef.current;
    }

    // [E] If a fetch is already in flight and this caller isn't forcing a
    // refresh, return the same promise instead of firing a second fan-out.
    if (!force && inFlightFilmsRef.current) {
      return inFlightFilmsRef.current;
    }

    const run = (async () => {
      try {
        setLoadingFilms(true);
        setError(null);
        // B1.2: the global GET /api/films endpoint was retired (it caused an N+1
        // query storm). We now build the cross-workspace film list by fanning
        // out over the user's workspaces using the fast per-workspace endpoint
        // (B1.1), concurrently (B2), and merging + de-duping by film id. This
        // preserves the cross-workspace FilmSwitcher UX without the slow call.
        const wsRes = await axios.get(`${API_BASE}/workspaces`, { headers: getAuthHeaders() });
        const userWorkspaces = Array.isArray(wsRes.data) ? wsRes.data : [];
        const perWorkspace = await Promise.all(
          userWorkspaces.map((ws) =>
            axios
              .get(`${API_BASE}/workspaces/${ws.id}/films`, { headers: getAuthHeaders() })
              .then((r) => (Array.isArray(r.data) ? r.data : []))
              .catch(() => [])
          )
        );
        const byId = {};
        for (const list of perWorkspace) {
          for (const film of list) {
            byId[film.id] = film;
          }
        }
        const merged = Object.values(byId);
        setFilms(merged);
        setFilmsLoaded(true);
        filmsLoadedRef.current = true;
        return merged;
      } catch (err) {
        console.error('Failed to fetch films:', err);
        setError(err.message);
        return filmsRef.current;
      } finally {
        setLoadingFilms(false);
        inFlightFilmsRef.current = null;
      }
    })();

    inFlightFilmsRef.current = run;
    return run;
  }, [userId]);

  // Fetch a specific film
  //
  // IMPORTANT: This callback MUST remain reference-stable across re-renders
  // (only depend on `userId`). Reading `filmCache` / `currentFilm` from
  // refs avoids recreating the callback every time those state slices
  // change — which would otherwise cascade through any downstream
  // `useCallback`/`useEffect` that depends on `fetchFilm` and cause an
  // infinite fetch loop (especially when force=true).
  const fetchFilm = useCallback(async (filmId, force = false) => {
    if (!userId || !filmId) return null;

    const cachedFilm = filmCacheRef.current[filmId];
    // Check cache first
    if (!force && cachedFilm) {
      // Avoid redundant state writes if currentFilm is already this one.
      if (!currentFilmRef.current || String(currentFilmRef.current.id) !== String(filmId)) {
        setCurrentFilm(cachedFilm);
      }
      return cachedFilm;
    }

    // Also if currentFilm is already this film, and not force, return it.
    const current = currentFilmRef.current;
    if (!force && current && String(current.id) === String(filmId)) {
      return current;
    }

    try {
      setLoadingCurrentFilm(true);
      setError(null);
      const res = await axios.get(`${API_BASE}/films/${filmId}`, { headers: getAuthHeaders() });
      const filmData = res.data;

      setCurrentFilm(filmData);
      setFilmCache(prev => ({ ...prev, [filmId]: filmData }));
      return filmData;
    } catch (err) {
      console.error(`Failed to fetch film ${filmId}:`, err);
      setError(err.message);
      return null;
    } finally {
      setLoadingCurrentFilm(false);
    }
  }, [userId]);

  // Update current film in cache and state (e.g. after edit)
  const updateFilmState = useCallback((updatedFilm) => {
    if (!updatedFilm) return;
    setCurrentFilm(updatedFilm);
    setFilmCache(prev => ({ ...prev, [updatedFilm.id]: updatedFilm }));
    setFilms(prev => prev.map(f => f.id === updatedFilm.id ? updatedFilm : f));
  }, []);

  // Invalidate the cached single-film entry so the next fetchFilm() goes
  // to the network. Used after any mutation that changes derived stats
  // (shot count, covered count, scene count) so the Film Dashboard does
  // not show stale numbers on the way back.
  const invalidateFilm = useCallback((filmId) => {
    if (!filmId) return;
    setFilmCache(prev => {
      if (!(filmId in prev)) return prev;
      const next = { ...prev };
      delete next[filmId];
      return next;
    });
    setCurrentFilm(prev => (prev && String(prev.id) === String(filmId) ? null : prev));
  }, []);

  // Invalidate the films list cache so the next fetchFilms() forces a refresh.
  const invalidateFilms = useCallback(() => {
    filmsLoadedRef.current = false;
    setFilmsLoaded(false);
  }, []);

  /* ── ID-based film setter (Phase 1) ────────────────────────────────
   * The new public API used by RouteContextBridge (URL → context) and
   * any future top-nav switcher. Persists to localStorage; never fires
   * a navigation by itself. Assumes caller has ensured the film
   * belongs to the current workspace — validation effect below will
   * catch any mismatch asynchronously.
   * ───────────────────────────────────────────────────────────────── */
  const setCurrentFilmId = useCallback((newId) => {
    setCurrentFilmIdState((prev) => {
      const next = newId || null;
      if (prev === next) return prev;
      writePersistedFilmId(next);
      return next;
    });
  }, []);

  // Clear current film (e.g. when leaving film section)
  const clearCurrentFilm = useCallback(() => {
    setCurrentFilmIdState((prev) => {
      if (prev !== null) writePersistedFilmId(null);
      return null;
    });
    setCurrentFilm(null);
  }, []);

  /* ── Cross-context: workspace switch clears the film ───────────────
   * If currentWorkspaceId changed but currentFilmId did NOT change in
   * the same render batch, the previously-selected film cannot belong
   * to the new workspace — clear it. We use refs (not state) to detect
   * the "what changed in this batch" delta because we need to compare
   * against the previous render's values without triggering a loop.
   *
   * If BOTH change in the same batch (e.g. deep-link navigation to a
   * different workspace+film via RouteContextBridge), we keep the film
   * — its workspace match is asserted by the validation effect below.
   * ───────────────────────────────────────────────────────────────── */
  const prevWsIdRef = useRef(currentWorkspaceId);
  const prevFilmIdRef = useRef(currentFilmId);
  useEffect(() => {
    const wsChanged = prevWsIdRef.current !== currentWorkspaceId;
    const filmChanged = prevFilmIdRef.current !== currentFilmId;

    if (wsChanged && !filmChanged && currentFilmId !== null) {
      setCurrentFilmIdState(null);
      writePersistedFilmId(null);
      setCurrentFilm(null);
    }

    prevWsIdRef.current = currentWorkspaceId;
    prevFilmIdRef.current = currentFilmId;
  }, [currentWorkspaceId, currentFilmId]);

  /* ── Validate persisted film against the current workspace ─────────
   * Runs whenever currentFilmId or currentWorkspaceId changes. Uses
   * fetchFilm (cache-first) to confirm the film still exists and
   * belongs to the active workspace. If not, clear silently.
   *
   * If currentWorkspaceId is not yet known (e.g. workspaces still
   * loading), we defer validation rather than clearing — the effect
   * will re-run once workspaceId is set.
   * ───────────────────────────────────────────────────────────────── */
  /* ── Validate persisted film against the current workspace ─────────
   * Runs whenever currentFilmId or currentWorkspaceId changes.
   *
   * The validation distinguishes "definitive" failures from "transient"
   * failures so that a brief network/CORS/5xx hiccup does NOT wipe the
   * persisted context (which would have been the cause of the
   * Phase 1 tester's Test 1 / Test 2 failure when the build was loaded
   * from a hostname that couldn't reach the API).
   *
   *   Clear (definitive):
   *     - GET /api/films/:id returns 404 → film genuinely doesn't exist
   *     - GET /api/films/:id returns 403 → user has lost access
   *     - GET /api/films/:id returns 200 but film.workspace_id mismatches
   *       the current workspace
   *
   *   Keep + warn (transient):
   *     - network error, timeout, CORS, 5xx, abort/cancel, no response
   *     - 200 with empty body (shouldn't happen — treat defensively)
   *
   * We bypass fetchFilm() here because it swallows ALL errors to `null`
   * for caller convenience, which would prevent us from distinguishing
   * the two cases above.
   * ───────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!currentFilmId) return undefined;
    if (!userId) return undefined;
    if (!currentWorkspaceId) return undefined;

    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/films/${currentFilmId}`, {
          headers: getAuthHeaders(),
          signal: controller.signal,
        });
        if (cancelled) return;
        const film = res.data;

        if (!film) {
          // 200 OK with empty body — defensive, treat as transient.
          console.warn('[FilmContext] film validation returned empty body — keeping currentFilmId');
          return;
        }

        if (film.workspace_id && film.workspace_id !== currentWorkspaceId) {
          // Definitive mismatch — film exists but belongs to a different workspace.
          setCurrentFilmIdState(null);
          writePersistedFilmId(null);
          setCurrentFilm(null);
          return;
        }

        // Validation succeeded — keep currentFilm in sync so consumers
        // (e.g. the sidebar's "Working in: {title}" label) have the
        // resolved object without needing to call fetchFilm themselves.
        setCurrentFilm(film);
      } catch (err) {
        if (cancelled) return;

        // Aborted by cleanup (component unmount / deps changed) — do nothing.
        if (
          axios.isCancel?.(err) ||
          err?.code === 'ERR_CANCELED' ||
          err?.name === 'CanceledError' ||
          err?.name === 'AbortError'
        ) {
          return;
        }

        const status = err?.response?.status;
        if (status === 404 || status === 403) {
          // Definitive: the film doesn't exist or the user has lost access.
          setCurrentFilmIdState(null);
          writePersistedFilmId(null);
          setCurrentFilm(null);
        } else {
          // Network error, 5xx, CORS, timeout, no-response — KEEP state.
          console.warn(
            '[FilmContext] transient validation failure — keeping currentFilmId',
            { filmId: currentFilmId, status: status ?? null, message: err?.message }
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [currentFilmId, currentWorkspaceId, userId]);

  const value = useMemo(() => ({
    films,
    currentFilm,
    currentFilmId,
    setCurrentFilmId,
    loadingFilms,
    loadingCurrentFilm,
    error,
    fetchFilms,
    fetchFilm,
    updateFilmState,
    invalidateFilm,
    invalidateFilms,
    clearCurrentFilm
  }), [
    films,
    currentFilm,
    currentFilmId,
    setCurrentFilmId,
    loadingFilms,
    loadingCurrentFilm,
    error,
    fetchFilms,
    fetchFilm,
    updateFilmState,
    invalidateFilm,
    invalidateFilms,
    clearCurrentFilm
  ]);

  return (
    <FilmContext.Provider value={value}>
      {children}
    </FilmContext.Provider>
  );
};

export const useFilm = () => {
  const context = useContext(FilmContext);
  if (!context) {
    throw new Error('useFilm must be used within a FilmProvider');
  }
  return context;
};

export default FilmContext;
